import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Building2, 
  User, 
  Users, 
  Mail, 
  Phone, 
  MapPin,
  UserPlus,
  Loader2,
  MoreVertical,
  Trash2,
  Shield,
  Clock,
  ListChecks
} from 'lucide-react';
import InviteDialog from '@/components/settings/InviteDialog';
import RoleChangeDialog from '@/components/settings/RoleChangeDialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, checkAuth } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  const [company, setCompany] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit states
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);

  // Invite dialog
  const [showInvite, setShowInvite] = useState(false);
  const [newInvite, setNewInvite] = useState({ email: '', role: 'Project Manager' });
  const [createdInvite, setCreatedInvite] = useState(null);

  // Role change dialog
  const [showRoleChange, setShowRoleChange] = useState(null);
  const [newRole, setNewRole] = useState('');

  // Waitlist
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [companyRes, teamRes] = await Promise.all([
        axios.get(`${API}/settings/company`, { withCredentials: true }),
        axios.get(`${API}/settings/team`, { withCredentials: true })
      ]);
      setCompany(companyRes.data);
      setTeam(teamRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      const res = await axios.get(`${API}/waitlist/admin`, { withCredentials: true });
      setWaitlist(res.data);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleTabChange = (val) => {
    setActiveTab(val);
    if (val === 'waitlist' && waitlist.length === 0) {
      fetchWaitlist();
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings/company`, editingCompany, { withCredentials: true });
      setCompany(response.data);
      setEditingCompany(null);
      toast.success('Company info updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings/profile`, { name: editingProfile.name }, { withCredentials: true });
      await checkAuth();
      setEditingProfile(null);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(`${API}/invites/create`, newInvite, { withCredentials: true });
      setCreatedInvite(response.data);
      // Refresh team to show pending invite
      fetchData();
      toast.success('Invite created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeRole = async () => {
    if (!showRoleChange || !newRole) return;
    setSaving(true);
    try {
      await axios.put(`${API}/settings/team/${showRoleChange.user_id}/role?role=${newRole}`, {}, { withCredentials: true });
      setTeam(team.map(m => m.user_id === showRoleChange.user_id ? { ...m, role: newRole } : m));
      setShowRoleChange(null);
      setNewRole('');
      toast.success('Role updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (member) => {
    const confirmMsg = member.status === 'Invited' 
      ? `Cancel invite for ${member.email}?`
      : `Remove ${member.name} from the team? They will lose access to all company data.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/settings/team/${member.user_id}`, { withCredentials: true });
      setTeam(team.filter(m => m.user_id !== member.user_id));
      toast.success(member.status === 'Invited' ? 'Invite cancelled' : 'Team member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/join/${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  const closeInviteDialog = () => {
    setShowInvite(false);
    setNewInvite({ email: '', role: 'Project Manager' });
    setCreatedInvite(null);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'Owner': return 'bg-primary text-zinc-900';
      case 'Project Manager': return 'bg-blue-500 text-white';
      case 'Foreman': return 'bg-zinc-500 text-white';
      default: return 'bg-zinc-100';
    }
  };

  const canManageTeam = user?.role === 'Owner' || user?.role === 'Project Manager';
  const canChangeRoles = user?.role === 'Owner';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="settings-page">
      <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide tracking-tight mb-6">Settings</h1>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-full h-14 border border-zinc-200 bg-white p-0 rounded-none mb-6">
          <TabsTrigger 
            value="company" 
            data-testid="company-tab"
            className="flex-1 h-full rounded-none font-bold uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-zinc-900 border-r-2 border-black"
          >
            <Building2 className="w-5 h-5 mr-2" />
            Company
          </TabsTrigger>
          <TabsTrigger 
            value="profile" 
            data-testid="profile-tab"
            className="flex-1 h-full rounded-none font-bold uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-zinc-900 border-r-2 border-black"
          >
            <User className="w-5 h-5 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger 
            value="team" 
            data-testid="team-tab"
            className={`flex-1 h-full rounded-none font-bold uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-zinc-900 ${user?.role === 'Owner' ? 'border-r-2 border-black' : ''}`}
          >
            <Users className="w-5 h-5 mr-2" />
            Team
          </TabsTrigger>
          {user?.role === 'Owner' && (
            <TabsTrigger 
              value="waitlist" 
              data-testid="waitlist-tab"
              className="flex-1 h-full rounded-none font-bold uppercase tracking-wide data-[state=active]:bg-primary data-[state=active]:text-zinc-900"
            >
              <ListChecks className="w-5 h-5 mr-2" />
              Waitlist
            </TabsTrigger>
          )}
        </TabsList>

        {/* Company Tab */}
        <TabsContent value="company" className="mt-0">
          <div className="bg-white border border-zinc-200 p-6 ">
            {editingCompany ? (
              <form onSubmit={handleSaveCompany} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2">Company Name</label>
                  <input
                    type="text"
                    value={editingCompany.name}
                    onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                    required
                    data-testid="company-name-edit"
                    className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2">Address</label>
                  <input
                    type="text"
                    value={editingCompany.address || ''}
                    onChange={(e) => setEditingCompany({ ...editingCompany, address: e.target.value })}
                    data-testid="company-address-edit"
                    className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
                    placeholder="123 Main St, City, ST 12345"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2">Phone</label>
                  <input
                    type="text"
                    value={editingCompany.phone || ''}
                    onChange={(e) => setEditingCompany({ ...editingCompany, phone: e.target.value })}
                    data-testid="company-phone-edit"
                    className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="button" 
                    onClick={() => setEditingCompany(null)}
                    className="flex-1 h-14 bg-white text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-zinc-50"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={saving}
                    data-testid="save-company-btn"
                    className="flex-1 h-14 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-primary border border-zinc-200 flex items-center justify-center">
                      <Building2 className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-xl font-heading font-bold uppercase tracking-wide" data-testid="company-name">{company?.name}</h2>
                      <p className="text-sm text-zinc-500">Company ID: {company?.company_id}</p>
                    </div>
                  </div>
                  {user?.role === 'Owner' && (
                    <button
                      onClick={() => setEditingCompany({ ...company })}
                      data-testid="edit-company-btn"
                      className="h-12 px-6 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1"
                    >
                      Edit
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-background border border-zinc-200">
                    <MapPin className="w-5 h-5 text-zinc-500" />
                    <div>
                      <p className="text-xs font-bold uppercase text-zinc-500">Address</p>
                      <p className="font-medium">{company?.address || 'Not set'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-background border border-zinc-200">
                    <Phone className="w-5 h-5 text-zinc-500" />
                    <div>
                      <p className="text-xs font-bold uppercase text-zinc-500">Phone</p>
                      <p className="font-medium">{company?.phone || 'Not set'}</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-0">
          <div className="bg-white border border-zinc-200 p-6 ">
            {editingProfile ? (
              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2">Full Name</label>
                  <input
                    type="text"
                    value={editingProfile.name}
                    onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })}
                    required
                    data-testid="profile-name-edit"
                    className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="button" 
                    onClick={() => setEditingProfile(null)}
                    className="flex-1 h-14 bg-white text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-zinc-50"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={saving}
                    data-testid="save-profile-btn"
                    className="flex-1 h-14 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name}
                        className="w-16 h-16 border border-zinc-200 object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-primary border border-zinc-200 flex items-center justify-center">
                        <User className="w-8 h-8" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-heading font-bold uppercase tracking-wide" data-testid="profile-name">{user?.name}</h2>
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getRoleBadgeClass(user?.role)}`}>
                        {user?.role}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingProfile({ name: user?.name })}
                    data-testid="edit-profile-btn"
                    className="h-12 px-6 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1"
                  >
                    Edit
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-background border border-zinc-200">
                    <Mail className="w-5 h-5 text-zinc-500" />
                    <div>
                      <p className="text-xs font-bold uppercase text-zinc-500">Email</p>
                      <p className="font-medium">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-background border border-zinc-200">
                    <Shield className="w-5 h-5 text-zinc-500" />
                    <div>
                      <p className="text-xs font-bold uppercase text-zinc-500">Role & Permissions</p>
                      <p className="font-medium">{user?.role}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {user?.role === 'Owner' && 'Full access to all features including billing and team management'}
                        {user?.role === 'Project Manager' && 'Create/edit projects, phases, orders. Invite team members.'}
                        {user?.role === 'Foreman' && 'View assigned projects, create orders, update phase status.'}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="mt-0">
          <div className="bg-white border border-zinc-200 p-6 ">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-heading font-bold uppercase tracking-wide">Team Members</h2>
                <p className="text-sm text-zinc-500">{team.filter(m => m.status === 'Active').length} active, {team.filter(m => m.status === 'Invited').length} pending</p>
              </div>
              {canManageTeam && (
                <button
                  onClick={() => setShowInvite(true)}
                  data-testid="invite-btn"
                  className="h-12 px-6 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1 flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite
                </button>
              )}
            </div>

            {/* Roles Legend */}
            <div className="mb-6 p-4 bg-zinc-50 border border-zinc-200">
              <p className="text-xs font-bold uppercase mb-2">Role Permissions</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div>
                  <span className={`px-2 py-0.5 font-bold uppercase border border-black ${getRoleBadgeClass('Owner')}`}>Owner</span>
                  <p className="mt-1 text-zinc-500">Full access, billing, team management</p>
                </div>
                <div>
                  <span className={`px-2 py-0.5 font-bold uppercase border border-black ${getRoleBadgeClass('Project Manager')}`}>PM</span>
                  <p className="mt-1 text-zinc-500">Create/edit projects, orders, invite team</p>
                </div>
                <div>
                  <span className={`px-2 py-0.5 font-bold uppercase border border-black ${getRoleBadgeClass('Foreman')}`}>Foreman</span>
                  <p className="mt-1 text-zinc-500">View assigned projects, create orders</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {team.map((member) => (
                <div 
                  key={member.user_id}
                  data-testid={`team-member-${member.user_id}`}
                  className={`flex items-center gap-4 p-4 border border-zinc-200 ${member.status === 'Invited' ? 'bg-yellow-50' : 'bg-background'}`}
                >
                  {member.picture ? (
                    <img 
                      src={member.picture} 
                      alt={member.name}
                      className="w-12 h-12 border border-zinc-200 object-cover"
                    />
                  ) : (
                    <div className={`w-12 h-12 border border-zinc-200 flex items-center justify-center ${member.status === 'Invited' ? 'bg-yellow-200' : 'bg-secondary'}`}>
                      {member.status === 'Invited' ? (
                        <Clock className="w-6 h-6 text-yellow-700" />
                      ) : (
                        <User className="w-6 h-6 text-white" />
                      )}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold">{member.name}</p>
                      {member.status === 'Invited' && (
                        <span className="px-2 py-0.5 text-xs font-bold uppercase bg-yellow-200 text-yellow-800 border border-yellow-500">
                          Invited
                        </span>
                      )}
                      {member.user_id === user?.user_id && (
                        <span className="px-2 py-0.5 text-xs font-bold uppercase bg-zinc-100 text-zinc-700 border border-gray-400">
                          You
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500">{member.email}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-bold uppercase border border-black ${getRoleBadgeClass(member.role)}`}>
                    {member.role}
                  </span>
                  
                  {/* Actions dropdown - only show for other members */}
                  {canChangeRoles && member.user_id !== user?.user_id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="h-10 w-10 flex items-center justify-center border border-zinc-200 bg-white hover:bg-zinc-50">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="border border-zinc-200 rounded-none ">
                        {member.status === 'Active' && (
                          <DropdownMenuItem 
                            onClick={() => {
                              setShowRoleChange(member);
                              setNewRole(member.role);
                            }}
                            className="cursor-pointer"
                          >
                            <Shield className="w-4 h-4 mr-2" />
                            Change Role
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleRemoveMember(member)}
                          className="cursor-pointer text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {member.status === 'Invited' ? 'Cancel Invite' : 'Remove'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Waitlist Tab (Owner only) */}
        {user?.role === 'Owner' && (
          <TabsContent value="waitlist" className="mt-0">
            <div className="bg-white border border-zinc-200 p-6" data-testid="waitlist-panel">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-heading font-bold uppercase tracking-wide">Marketplace Waitlist</h2>
                  <p className="text-sm text-zinc-500">{waitlist.length} {waitlist.length === 1 ? 'entry' : 'entries'}</p>
                </div>
                <button
                  onClick={fetchWaitlist}
                  disabled={waitlistLoading}
                  data-testid="refresh-waitlist-btn"
                  className="h-10 px-4 bg-white text-zinc-700 font-heading font-semibold uppercase tracking-wider text-xs border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50"
                >
                  {waitlistLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {waitlistLoading && waitlist.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
                </div>
              ) : waitlist.length === 0 ? (
                <div className="text-center py-12">
                  <ListChecks className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                  <p className="text-zinc-500 text-sm">No waitlist entries yet.</p>
                </div>
              ) : (
                <div className="border border-zinc-200 overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-12 bg-zinc-50 border-b border-zinc-200 px-4 py-2">
                    <div className="col-span-4 text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400">Email</div>
                    <div className="col-span-4 text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400">Company</div>
                    <div className="col-span-4 text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400">Signup Date</div>
                  </div>
                  {/* Table rows */}
                  {waitlist.map((entry, i) => (
                    <div
                      key={entry.email + i}
                      data-testid={`waitlist-entry-${i}`}
                      className={`grid grid-cols-12 px-4 py-3 border-b border-zinc-100 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50/50'}`}
                    >
                      <div className="col-span-4 flex items-center gap-2 min-w-0">
                        <Mail className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                        <span className="text-sm text-zinc-900 truncate">{entry.email}</span>
                      </div>
                      <div className="col-span-4 text-sm text-zinc-600 truncate">{entry.company_name || 'N/A'}</div>
                      <div className="col-span-4 text-sm font-mono text-zinc-500">
                        {entry.signup_date ? new Date(entry.signup_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <InviteDialog
        open={showInvite}
        onClose={closeInviteDialog}
        newInvite={newInvite}
        setNewInvite={setNewInvite}
        createdInvite={createdInvite}
        saving={saving}
        onSubmit={handleCreateInvite}
        onCopyCode={copyInviteCode}
        onCopyLink={copyInviteLink}
      />

      <RoleChangeDialog
        member={showRoleChange}
        newRole={newRole}
        setNewRole={setNewRole}
        saving={saving}
        onClose={() => setShowRoleChange(null)}
        onConfirm={handleChangeRole}
      />
    </div>
  );
};

export default SettingsPage;
